package org.example.project.enginesmartring

import androidx.compose.runtime.*
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.pow
import kotlin.math.sin
import kotlin.math.sqrt
import org.example.project.dashboard.DashboardTheme
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.w3c.dom.events.MouseEvent

@Composable
fun EngineSmartRingLayout(
    slots: List<All?>,
    nestedSlotsByKey: Map<String, List<All>> = emptyMap(),
    anchorX: Double,
    anchorY: Double,
    onSelect: (String) -> Unit,
    onClose: () -> Unit
) {
    val scope = rememberCoroutineScope()
    var flashingKey by remember { mutableStateOf<String?>(null) }
    var hoveredParentKey by remember { mutableStateOf<String?>(null) }
    var nestedAreaArmed by remember { mutableStateOf(false) }
    val hasNestedRing = nestedSlotsByKey.isNotEmpty()
    val visiblePrimarySlots = remember(slots, hoveredParentKey) {
        if (hoveredParentKey == null) {
            slots
        } else {
            slots.map { slot -> slot?.takeIf { it.key == hoveredParentKey } }
        }
    }
    val primaryCount = visiblePrimarySlots.count { it != null }.coerceAtLeast(1)
    val ringRadiusPx = if (hasNestedRing) 96 else 128
    val nestedRingRadiusPx = 164
    val centerPx = if (hasNestedRing) 190 else 150
    val nestedRingInnerBound = (nestedRingRadiusPx - 44).toDouble()
    val nestedRingOuterBound = (nestedRingRadiusPx + 44).toDouble()

    Style(EngineSmartRingStyles)

    Div({
        classes(EngineSmartRingStyles.overlayBackdrop)
        onClick { onClose() }
    }) {
        Div({
            classes(EngineSmartRingStyles.overlayPanel)
            style {
                left((anchorX - centerPx).px)
                top((anchorY - centerPx).px)
                width((centerPx * 2).px)
                height((centerPx * 2).px)
            }
            onClick { it.stopPropagation() }
        }) {
            Div({
                classes(EngineSmartRingStyles.overlayCanvas)
                style {
                    width((centerPx * 2).px)
                    height((centerPx * 2).px)
                }
                onMouseMove { event ->
                    if (hoveredParentKey == null) return@onMouseMove
                    val mouseEvent = event.nativeEvent as? MouseEvent ?: return@onMouseMove
                    val rect = mouseEvent.currentTarget.asDynamic().getBoundingClientRect()
                    val relativeX = mouseEvent.clientX - rect.left as Double
                    val relativeY = mouseEvent.clientY - rect.top as Double
                    val distance = sqrt((relativeX - centerPx).pow(2) + (relativeY - centerPx).pow(2))
                    val insideNestedBand = distance in nestedRingInnerBound..nestedRingOuterBound
                    nestedAreaArmed = insideNestedBand
                    if (!insideNestedBand && hoveredParentKey != null) {
                        hoveredParentKey = null
                    }
                }
                onMouseLeave {
                    nestedAreaArmed = false
                    hoveredParentKey = null
                }
            }) {
                visiblePrimarySlots.forEachIndexed { index, screen ->
                    if (screen == null) return@forEachIndexed
                    val angle = (-90.0 + (360.0 / primaryCount) * index) * (PI / 180.0)
                    val leftPx = centerPx + cos(angle) * ringRadiusPx - 34.0
                    val topPx = centerPx + sin(angle) * ringRadiusPx - 34.0
                    val nestedSlots = nestedSlotsByKey[screen.key].orEmpty()

                    key(screen.key) {
                        var isHovered by remember { mutableStateOf(false) }
                        val isFlashing = flashingKey == screen.key

                        Div({
                            classes(EngineSmartRingStyles.overlaySlot)
                            style {
                                left(leftPx.px)
                                top(topPx.px)
                                property(
                                    "border",
                                    if (isHovered || isFlashing) "1px solid ${DashboardTheme.ACCENT}" else "1px solid ${DashboardTheme.BORDER_LIGHT}"
                                )
                                property(
                                    "box-shadow",
                                    if (isFlashing) "0 0 18px rgba(255, 0, 0, 0.45)" else if (isHovered) "0 0 14px rgba(255, 0, 0, 0.20)" else "none"
                                )
                            }
                            onMouseEnter {
                                isHovered = true
                                if (nestedSlots.isNotEmpty()) {
                                    hoveredParentKey = screen.key
                                    nestedAreaArmed = false
                                }
                            }
                            onMouseLeave {
                                isHovered = false
                                if (nestedSlots.isNotEmpty()) {
                                    scope.launch {
                                        delay(80)
                                        if (!nestedAreaArmed && hoveredParentKey == screen.key) {
                                            hoveredParentKey = null
                                        }
                                    }
                                }
                            }
                            onClick {
                                if (nestedSlots.isNotEmpty()) return@onClick
                                if (flashingKey != null) return@onClick
                                flashingKey = screen.key
                                scope.launch {
                                    delay(120)
                                    onClose()
                                    onSelect(screen.key)
                                    flashingKey = null
                                }
                            }
                        }) {
                            Img(screen.icon.ifBlank { "images/step.svg" }) {
                                classes(EngineSmartRingStyles.overlaySlotIcon)
                                style {
                                    property(
                                        "filter",
                                        if (isHovered || isFlashing) DashboardTheme.ICON_FILTER_ACTIVE else DashboardTheme.ICON_FILTER_WHITE
                                    )
                                }
                            }
                        }
                    }
                }

                val nestedSlots = hoveredParentKey?.let { nestedSlotsByKey[it] }.orEmpty()
                nestedSlots.forEachIndexed { index, screen ->
                    val nestedCount = nestedSlots.size.coerceAtLeast(1)
                    val angle = (-90.0 + (360.0 / nestedCount) * index) * (PI / 180.0)
                    val leftPx = centerPx + cos(angle) * nestedRingRadiusPx - 34.0
                    val topPx = centerPx + sin(angle) * nestedRingRadiusPx - 34.0

                    key("nested_${screen.key}") {
                        var isHovered by remember { mutableStateOf(false) }
                        val isFlashing = flashingKey == screen.key

                        Div({
                            classes(EngineSmartRingStyles.overlaySlot)
                            style {
                                left(leftPx.px)
                                top(topPx.px)
                                property(
                                    "border",
                                    if (isHovered || isFlashing) "1px solid ${DashboardTheme.ACCENT}" else "1px solid ${DashboardTheme.BORDER_LIGHT}"
                                )
                                property(
                                    "box-shadow",
                                    if (isFlashing) "0 0 18px rgba(255, 0, 0, 0.45)" else if (isHovered) "0 0 14px rgba(255, 0, 0, 0.20)" else "none"
                                )
                            }
                            onMouseEnter {
                                isHovered = true
                                nestedAreaArmed = true
                            }
                            onMouseLeave {
                                isHovered = false
                            }
                            onClick {
                                if (flashingKey != null) return@onClick
                                flashingKey = screen.key
                                scope.launch {
                                    delay(120)
                                    onClose()
                                    onSelect(screen.key)
                                    flashingKey = null
                                }
                            }
                        }) {
                            Img(screen.icon.ifBlank { "images/step.svg" }) {
                                classes(EngineSmartRingStyles.overlaySlotIcon)
                                style {
                                    property(
                                        "filter",
                                        if (isHovered || isFlashing) DashboardTheme.ICON_FILTER_ACTIVE else DashboardTheme.ICON_FILTER_WHITE
                                    )
                                }
                            }
                        }
                    }
                }
                Div({
                    classes(EngineSmartRingStyles.overlayCenter)
                    onClick { onClose() }
                }) {
                    Img("images/close.svg") {
                        classes(EngineSmartRingStyles.overlayCenterIcon)
                        style { property("filter", DashboardTheme.ICON_FILTER_WHITE) }
                    }
                }
            }
        }
    }
}

object EngineSmartRingStyles : StyleSheet() {
    val overlayBackdrop by style {
        position(Position.Fixed)
        left(0.px)
        top(0.px)
        right(0.px)
        bottom(0.px)
        property("z-index", "9800")
    }
    val overlayPanel by style {
        position(Position.Fixed)
        width(300.px)
        height(300.px)
        property("z-index", "9801")
        property("pointer-events", "auto")
    }
    val overlayCanvas by style {
        position(Position.Relative)
        width(300.px)
        height(300.px)
    }
    val overlaySlot by style {
        position(Position.Absolute)
        width(68.px)
        height(68.px)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        padding(6.px)
        property("box-sizing", "border-box")
        property("border", "1px solid ${DashboardTheme.BORDER_LIGHT}")
        borderRadius(50.percent)
        property("background", DashboardTheme.PREMIUM_SURFACE_BG)
        cursor("pointer")
    }
    val overlaySlotIcon by style {
        width(24.px)
        height(24.px)
        display(DisplayStyle.Block)
    }
    val overlayCenter by style {
        position(Position.Absolute)
        left(50.percent)
        top(50.percent)
        property("transform", "translate(-50%, -50%)")
        width(80.px)
        height(80.px)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        property("border", "1px solid ${DashboardTheme.BORDER_LIGHT}")
        borderRadius(50.percent)
        property("background", DashboardTheme.PREMIUM_SURFACE_BG)
        cursor("pointer")
    }
    val overlayCenterIcon by style {
        width(18.px)
        height(18.px)
        display(DisplayStyle.Block)
    }
}
